import { motion, useAnimation, useInView } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { qualificationsData } from './Data';
import './qualification.css';

const Qualification = () => {
    const [toggleState, setToggleState] = useState(2);
    const ref = useRef(null);
    const isInView = useInView(ref, { 
        once: false,
        threshold: 0.2
    });
    const controls = useAnimation();

    useEffect(() => {
        if (isInView) {
            controls.start('visible');
        }
    }, [isInView, controls]);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    const staggerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.3
            }
        }
    };

    const itemVariants = {
        hidden: { 
            opacity: 0, 
            y: 50,
            scale: 0.95
        },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                type: "spring",
                damping: 15,
                stiffness: 100
            }
        }
    };

    const lineVariants = {
        hidden: { height: 0 },
        visible: { 
            height: '100%',
            transition: { duration: 0.5, delay: 0.2 }
        }
    };

    const roundVariants = {
        hidden: { scale: 0 },
        visible: { 
            scale: 1,
            transition: { 
                type: "spring",
                stiffness: 300,
                damping: 15
            }
        }
    };

    const toggleTab = (index) => {
        setToggleState(index);
    }

    return (
        <motion.section 
            className="qualification section"
            ref={ref}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            variants={containerVariants}
        >
            <motion.h2 
                className="section__title"
                variants={itemVariants}
            >
                Qualification
            </motion.h2>
            <motion.span 
                className="section__subtitle"
                variants={itemVariants}
            >
                My personal journey
            </motion.span>

            <motion.div 
                className='qualification__container'
                variants={containerVariants}
            >
                <motion.div 
                    className='qualification__tabs'
                    variants={itemVariants}
                >
                    <div 
                        className={toggleState === 2 ? 'qualification__button qualification__active button--flex' : 'qualification__button button--flex'} 
                        onClick={() => toggleTab(2)} 
                    >
                        <i className="uil uil-briefcase-alt qualification__icon"></i>
                        Experience
                    </div>

                    <div 
                        className={toggleState === 1 ? 'qualification__button qualification__active button--flex' : 'qualification__button button--flex'} 
                        onClick={() => toggleTab(1)} 
                    >
                        <i className="uil uil-graduation-cap qualification__icon"></i>
                        Education
                    </div>
                </motion.div>

                <motion.div 
                    className="qualification__sections"
                    variants={staggerVariants}
                >
                    {[1, 2].map(tabIndex => (
                        <motion.div 
                            key={tabIndex}
                            className={toggleState === tabIndex ? 
                                'qualification__content qualification__content-active' : 
                                'qualification__content'}
                            variants={staggerVariants}
                            initial="hidden"
                            animate={toggleState === tabIndex ? "visible" : "hidden"}
                        >
                            {(tabIndex === 1 ? qualificationsData.education : qualificationsData.experience)
                                .map((item, index) => (
                                <motion.div 
                                    className="qualification__data" 
                                    key={item.id}
                                    variants={itemVariants}
                                    custom={index}
                                    initial="hidden"
                                    animate={toggleState === tabIndex ? "visible" : "hidden"}
                                    transition={{ delay: index * 0.2 }}
                                >
                                    {index % 2 === 0 ? (
                                        <>
                                            <motion.div
                                                variants={itemVariants}
                                                custom={index}
                                            >
                                                <motion.h3 
                                                    className="qualification__title"
                                                    initial={{ opacity: 0, x: -20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: 0.2 * index }}
                                                >
                                                    {item.title}
                                                </motion.h3>
                                                <motion.span 
                                                    className="qualification__subtitle"
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    transition={{ delay: 0.3 * index }}
                                                >
                                                    {item.subtitle}
                                                </motion.span>
                                                <motion.div 
                                                    className="qualification__calendar"
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    transition={{ delay: 0.4 * index }}
                                                >
                                                    <i className="uil uil-calendar-alt">  </i>
                                                    {item.calendar}
                                                </motion.div>
                                            </motion.div>

                                            <div>
                                                <motion.span 
                                                    className="qualification__rounder"
                                                    variants={roundVariants}
                                                />
                                                <motion.span 
                                                    className="qualification__line"
                                                    variants={lineVariants}
                                                />
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div></div>
                                            <div>
                                                <motion.span 
                                                    className="qualification__rounder"
                                                    variants={roundVariants}
                                                />
                                                <motion.span 
                                                    className="qualification__line"
                                                    variants={lineVariants}
                                                />
                                            </div>
                                            <motion.div
                                                variants={itemVariants}
                                                custom={index}
                                            >
                                                <motion.h3 
                                                    className="qualification__title"
                                                    initial={{ opacity: 0, x: -20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: 0.2 * index }}
                                                >
                                                    {item.title}
                                                </motion.h3>
                                                <motion.span 
                                                    className="qualification__subtitle"
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    transition={{ delay: 0.3 * index }}
                                                >
                                                    {item.subtitle}
                                                </motion.span>
                                                <motion.div 
                                                    className="qualification__calendar"
                                                    initial={{ opacity: 0 }}
                                                    animate={{ opacity: 1 }}
                                                    transition={{ delay: 0.4 * index }}
                                                >
                                                    <i className="uil uil-calendar-alt">  </i>
                                                    {item.calendar}
                                                </motion.div>
                                            </motion.div>
                                        </>
                                    )}
                                </motion.div>
                            ))}
                        </motion.div>
                    ))}
                </motion.div>
            </motion.div>
        </motion.section>
    );
}

export default Qualification;